import { CareEnvironment, CarePhase } from '../../../../appModel';
import {
  calcCareMattersDeathBenefit,
  calcRiderMonthly,
  calcTotalLtcBenefitOverPeriod,
} from '../../calcCareMattersCoverage';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH } from '../sharedConstants';
import {
  computeMonthlyCostMetrics,
  computeNon2AdlDaysThisRow,
} from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  getBenefitPeriodMonths,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
  getPhaseCareMonthsByPhase,
  getRiderForm,
  getWaitingPeriodBucketForEnvironment,
  resolveRowPolicyYear,
} from '../sharedPolicyConfig';
import { applyIndemnitySurplus } from '../sharedSurplus';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import { Claimant, JointWaitingPeriod, seedJointWaitingPeriod } from '../jointClaimant';

// ---------------------------------------------------------------------------
// CM II strategy state + deltas.
//
// `strategyState` carries the per-claim/per-month state that's specific to
// CareMatters II — the indemnity-surplus reserve and the per-claim EP latch.
// `strategyDeltas` carries the per-month flows that are also CM II–specific:
// the base/rider split of the monthly payout and the EP retroactive lump
// line items.
//
// RIDER CLOCK (P2-1) — the inflation rider escalates on the ANNIVERSARY
// (calendar) clock, `displayPolicyYear`, matching the carrier: the
// illustration (p.8 footnote) steps the rider on policy anniversaries
// regardless of claim status. This is an ACCEPTED DIVERGENCE from the legacy
// engine, whose `simulateCareMattersPhase` walked the rider on PAID months
// (pausing during the EP / non-paying stretches and permanently lagging the
// carrier's schedule — ~$5,600 lifetime underpay on the example policy with
// a 90-day EP). The old paid-month clock and its `phasePaidMonthsSoFar`
// counter are gone; there is exactly one policy-year clock per row.
// ---------------------------------------------------------------------------

// Per-claimant EP latch — used per spouse on joint policies.
export interface CmTwoClaimantState {
  // Whether the retroactive EP reimbursement lump has already fired. The EP is
  // reimbursed ONCE per claim — not once per care-environment WP bucket (an
  // asymmetric policy, e.g. home 60 / facility 90, must not fire it twice).
  epReimbursed: boolean;
}

export interface CmTwoState {
  // Shared across both spouses on a joint policy (legacy parity — A1).
  indemnitySurplus: number;
  // Single-life EP latch (flat). On joint rows this mirrors the active
  // claimant so the UI/glossary reads stay meaningful; the `joint` sub-object
  // is the source of truth (A9).
  epReimbursed: boolean;
  // Joint only: per-claimant EP latch.
  joint?: { client: CmTwoClaimantState; partner: CmTwoClaimantState };
  // Joint only: per-claimant waiting-period balances (each spouse serves their
  // own EP). Single-life uses the universal MonthlyState.waitingPeriod slot.
  jointWaitingPeriod?: JointWaitingPeriod;
}

export interface CmTwoDeltas {
  baseBenefitPaid: number;
  riderBenefitPaid: number;
  baseBenefitDays: number;
  riderBenefitDays: number;
  indemnitySurplusAccrued: number;
  indemnitySurplusApplied: number;
  // Total dollars paid out by the EP retroactive WP lump this month (base
  // portion + rider portion). Used to compute cumulative payout and the
  // "WP reimbursement" UI line.
  epLumpSumPaid: number;
  // Base-pool-depleting portion of the EP lump (days × baseDaily). Only
  // this portion reduces the base pool / deathBenefit — the rider portion
  // is paid on top, mirroring the monthly base+rider model.
  epLumpSumBasePaid: number;
  epLumpSumDays: number;
  epLumpSumDailyRate: number;
  // Portion of the EP lump that covered THIS row's care cost. The excess
  // (lump − applied) is banked into the indemnity surplus reserve instead of
  // riding as negative ineligible cost (P2-4b). Totals count the lump's LTC
  // contribution as `appliedToCost` + later surplus drains — never both.
  epLumpSumAppliedToCost: number;
}

// CM II's retroactive WP lump. Fires on the first month where home OR
// facility WP transitions from positive → 0. The lump pays
// `daysServed × (baseDaily + riderDaily)` total; only the base portion
// (`daysServed × baseDaily`) depletes the base pool, matching CM II's
// regular monthly pay model where the rider sits on top of the pool.
// Cumulative days-served is reconstructed from initial − current on
// whichever pool started larger (both decrement equally per month, so the
// larger pool is the canonical counter; the smaller hits 0 first and stops
// contributing). When the pool can't cover the full served-days at base
// daily, the lump scales proportionally — fewer days are funded and both
// base and rider stop together for the unfunded tail.
interface EpLumpInputs {
  ltcPolicy: PolicyFundingSource;
  careEnvironment: CareEnvironment;
  // The active claimant's WP balance before this row (single-life: the
  // universal slot; joint: the active spouse's per-claimant balance).
  waitingPeriodBefore: WaitingPeriodState;
  closeHomeWp: number;
  closeFacilityWp: number;
  baseBenefitPoolAfterBase: number;
  baseMonthly: number;
  // Rider monthly at the policy year of WP satisfaction. Year 1 is $0 (the
  // rider only steps up after the first anniversary — see `calcRiderMonthly`).
  riderMonthly: number;
  // Whether the lump has already fired earlier in the claim. The EP is
  // reimbursed once per claim, so a second bucket satisfying later must not
  // fire again.
  alreadyReimbursed: boolean;
}

interface EpLumpResult {
  epLumpSumPaid: number;
  epLumpSumBasePaid: number;
  epLumpSumDays: number;
  epLumpSumDailyRate: number;
  // True on the row where the lump fires (the EP's first satisfaction while
  // retroactive reimbursement is enabled). Flips the per-claim `epReimbursed`
  // latch so it never fires again.
  fired: boolean;
}

function computeEpRetroactiveLump(input: EpLumpInputs): EpLumpResult {
  const {
    ltcPolicy,
    careEnvironment,
    waitingPeriodBefore,
    closeHomeWp,
    closeFacilityWp,
    baseBenefitPoolAfterBase,
    baseMonthly,
    riderMonthly,
    alreadyReimbursed,
  } = input;

  // Fire when the active environment's elimination-period bucket is fully
  // satisfied for this month — but only once per claim (per claimant on joint).
  // Whichever environment's WP satisfies first triggers it; a later bucket
  // satisfaction must not fire it again.
  const wpBucket = getWaitingPeriodBucketForEnvironment(careEnvironment);
  const openRelevantWp =
    wpBucket === 'homeDays' ? waitingPeriodBefore.homeDays : waitingPeriodBefore.facilityDays;
  const closeRelevantWp = wpBucket === 'homeDays' ? closeHomeWp : closeFacilityWp;
  const epJustSatisfied = openRelevantWp > 0 && closeRelevantWp === 0;
  const epEligible =
    ltcPolicy.hasRetroactiveWaitingPeriodReimbursement === true &&
    epJustSatisfied &&
    !alreadyReimbursed;

  if (!epEligible) {
    return {
      epLumpSumPaid: 0,
      epLumpSumBasePaid: 0,
      epLumpSumDays: 0,
      epLumpSumDailyRate: 0,
      fired: false,
    };
  }

  // Mirror the same Remaining ?? Days ?? 0 fallback chain that initClaimState
  // uses to seed the WP balance. For a not-yet-on-claim policy `…Remaining`
  // is null but `…Days` carries the configured WP — without the fallback
  // here, cumulativeWpDaysServed evaluates to 0 and the lump silently zeros.
  const initialHomeWp =
    ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
    ltcPolicy.homeCareWaitingPeriodDays ??
    0;
  const initialFacilityWp =
    ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
    ltcPolicy.facilityCareWaitingPeriodDays ??
    0;
  const initialRelevantWp = wpBucket === 'homeDays' ? initialHomeWp : initialFacilityWp;
  const cumulativeWpDaysServed = Math.max(initialRelevantWp - closeRelevantWp, 0);

  const baseDaily = baseMonthly / DAYS_IN_MONTH;
  const riderDaily = riderMonthly / DAYS_IN_MONTH;
  const inflatedDaily = baseDaily + riderDaily;

  // Cap by base pool: pool can fund at most `pool / baseDaily` days at the
  // base rate. Rider scales together with base — both stop on the same day.
  const maxDaysFundable = baseDaily > 0 ? baseBenefitPoolAfterBase / baseDaily : 0;
  const daysFunded = Math.min(cumulativeWpDaysServed, Math.max(maxDaysFundable, 0));
  const epLumpSumBasePaid = daysFunded * baseDaily;
  const epLumpSumPaid = daysFunded * inflatedDaily;

  return {
    epLumpSumPaid,
    epLumpSumBasePaid,
    epLumpSumDays: daysFunded,
    epLumpSumDailyRate: inflatedDaily,
    fired: true,
  };
}

export const cmTwoStrategy: PolicyProjectionStrategy<CmTwoState, CmTwoDeltas> = {
  initClaimState(_client, ltcPolicy, jointAnchorPolicyYear): MonthlyState<CmTwoState> {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const benefitPeriodMonths = getBenefitPeriodMonths(ltcPolicy);
    const totalBasePool = baseMonthly * benefitPeriodMonths;
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const basePool = Math.max(totalBasePool - utilizedToDate, 0);
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;

    // `…WaitingPeriodDaysRemaining` is the on-claim runtime counter — set only
    // after the client has begun a claim. For a projection of a not-yet-on-claim
    // policy it is null, so fall back to the configured WP days; without this
    // the projection would pay from month 1 instead of serving the EP first.
    const waitingPeriod: WaitingPeriodState = {
      homeDays:
        ltcPolicy.homeCareWaitingPeriodDaysRemaining ?? ltcPolicy.homeCareWaitingPeriodDays ?? 0,
      facilityDays:
        ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
        ltcPolicy.facilityCareWaitingPeriodDays ??
        0,
    };

    // CM II's base pool is flat (no anniversary inflation), so the anchor is
    // only used to detect a joint run and seed per-claimant state.
    const isJoint = jointAnchorPolicyYear != null;
    return {
      pools: {
        baseBenefitPool: basePool,
        extensionOfBenefitsPool: 0,
        deathBenefit: Math.max(faceAmount - utilizedToDate, minGuaranteed),
      },
      waitingPeriod,
      strategyState: isJoint
        ? {
            indemnitySurplus: 0,
            epReimbursed: false,
            joint: {
              client: { epReimbursed: false },
              partner: { epReimbursed: false },
            },
            jointWaitingPeriod: seedJointWaitingPeriod(waitingPeriod),
          }
        : { indemnitySurplus: 0, epReimbursed: false },
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const riderForm = getRiderForm(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const policyPremiumStartYear = ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear;
    const firstPhaseStartYear = getFirstContributingPhaseStartYear(clientAsClient);
    // Lifetime LTC benefit (full-utilization, rider-aware), matching the legacy
    // `calcPolicySummaryStats` source. CM II's per-year snapshot
    // `effectiveTotalLtcBenefit` is a MONTHLY figure, so the adapter would
    // otherwise surface one month's benefit as the policy's total LTC benefit.
    // Claim starts in the policy year of the first contributing phase (matches
    // the projection's first claiming row); pre-policy onsets floor at year 1.
    const claimStartPolicyYear = Math.max(1, firstPhaseStartYear - policyPremiumStartYear + 1);
    const lifetimeLtcBenefit = calcTotalLtcBenefitOverPeriod({
      baseMonthly,
      benefitPeriodMonths: getBenefitPeriodMonths(ltcPolicy),
      claimStartPolicyYear,
      riderForm,
    });
    // Prior utilization deducted from the flat base pool at claim start, capped
    // at the pool (mirrors `initClaimState`'s `Math.max(totalBasePool −
    // utilized, 0)`). The death-benefit reduction is a separate concern — only
    // the LTC base pool feeds the "Drawn to date" column.
    const totalBasePool = baseMonthly * getBenefitPeriodMonths(ltcPolicy);
    const priorUtilization = Math.min(ltcPolicy.policyBenefitUtilizedToDate ?? 0, totalBasePool);
    return {
      strategyId: 'cm-two',
      priorUtilization,
      lifetimeLtcBenefit,
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      // Resolve every environment through the same helper the payout math
      // uses (getMonthlyBenefitAmountForEnvironment → getDailyBenefitAmountForEnvironment).
      // A raw `?? 0` here would turn a sparse/unpopulated env field into a hard
      // $0 that the presentation's `?? nominalDailyBenefitBase` can't rescue (0
      // isn't nullish), so the table would show a $0 cap while the math paid at
      // the resolved max. The helper honors an explicit 0 ("not covered") and
      // falls back to the max populated daily for sparse imports.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: getDailyBenefitAmountForEnvironment(ltcPolicy, CareEnvironment.home),
        [CareEnvironment.assistedLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      inflationRate: riderForm.rate,
      inflationCapYears: riderForm.capYears ?? 0,
      inflationIsSimple: riderForm.simple,
      // baseBenefitPool / extensionOfBenefitsPool inflation knobs are
      // intentionally omitted — CM II's base pool is flat (no per-pool
      // inflation concept; rider escalation lives on `inflationRate`
      // above), and CM II has no EOB pool.
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear,
      policyPremiumStartYear,
      minimumGuaranteedDeathBenefit: ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0,
      isReimbursement: false,
      isIndemnity: true,
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear } = input;
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const riderForm = getRiderForm(ltcPolicy);
    const riderMonthly = calcRiderMonthly(baseMonthly, policyYear, riderForm);
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0; // CM II flat
    const totalMonthlyBenefit = baseMonthly + riderMonthly;
    // CM II's base benefit pool is flat — it does not inflate (the rider
    // grows the monthly benefit instead). Its theoretical lifetime ceiling is
    // the constant base-monthly × benefit-period months.
    const benefitPeriodMonths = getBenefitPeriodMonths(ltcPolicy);
    return {
      policyYear,
      effectiveDailyBenefit: dailyBenefit + riderMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: dailyBenefit,
      effectiveDailyBenefitRider: riderMonthly / DAYS_IN_MONTH,
      effectiveFaceAmount: faceAmount,
      effectiveContinuationBenefit: 0,
      effectiveTotalLtcBenefit: totalMonthlyBenefit,
      monthlyBenefitCap: totalMonthlyBenefit,
      theoreticalLtcPoolCeiling: baseMonthly * benefitPeriodMonths,
    };
  },

  computeMonth(
    input: MonthComputeInput<CmTwoState, CmTwoDeltas>,
  ): MonthlyEntry<CmTwoState, CmTwoDeltas> {
    const { client, ltcPolicy, phaseCarePlan, monthlyCareInput, openState, priorClaimantMonth } =
      input;
    const { monthIndex, carePhase, monthsIntoPhase, claimant } = monthlyCareInput;
    const clientAsClient = client as Client;

    // Joint-ness is read from the carried state (per-claimant maps present), not
    // the row tag, so a joint run always preserves both spouses' state.
    const jointBefore = openState.strategyState.joint;
    const jointWaitingPeriodBefore = openState.strategyState.jointWaitingPeriod;
    const activeClaimant: Claimant = claimant ?? 'client';
    // The active claimant's EP latch before this row.
    const claimantBefore: CmTwoClaimantState = jointBefore
      ? jointBefore[activeClaimant]
      : { epReimbursed: openState.strategyState.epReimbursed };

    // Each spouse serves their own EP: joint reads the active claimant's balance
    // from strategyState (the universal waitingPeriod slot is rewritten per row).
    const waitingPeriodBefore: WaitingPeriodState = jointWaitingPeriodBefore
      ? jointWaitingPeriodBefore[activeClaimant]
      : openState.waitingPeriod;

    // The row's openState surfaces the active claimant's WP-before so identity
    // checks + UI read per-claimant. Shared pools + surplus pass through.
    const rowOpenState: MonthlyState<CmTwoState> = jointWaitingPeriodBefore
      ? { ...openState, waitingPeriod: waitingPeriodBefore }
      : openState;

    // Per-month effective care cost (annual-stepped from today, anchored at the
    // phase's predicted start year). Needed up front for the pre-policy gate.
    const costMetrics = computeMonthlyCostMetrics(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );

    // STRUCTURAL policy-year clock for the row (single monotonic calendar clock
    // — see the riderPolicyYear/displayPolicyYear note below). Resolved early so
    // the pre-policy gate can run before any WP/ADL classification.
    const displayPolicyYear = resolveRowPolicyYear(
      clientAsClient,
      monthIndex,
      input.jointAnchorPolicyYear,
    );

    const context: MonthContext = {
      monthIndex,
      policyYear: displayPolicyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, displayPolicyYear),
      claimYearOffset: Math.floor(monthIndex / 12),
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      claimant: monthlyCareInput.claimant,
    };

    // Pre-policy gate: a policy cannot pay for care delivered before its
    // premium-start year (policy year < 1). Book the full cost out-of-pocket
    // and carry every balance forward untouched — no pool drawdown, no EP
    // consumption, no DB reduction. Placed before WP/ADL classification and all
    // payout math. (Single-life floors policy year at 1 upstream, so only joint
    // partner rows can reach here.)
    if (displayPolicyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<CmTwoState, CmTwoDeltas>({
        context,
        facts: prePolicyFacts,
        openState: rowOpenState,
        strategyDeltas: {
          baseBenefitPaid: 0,
          riderBenefitPaid: 0,
          baseBenefitDays: 0,
          riderBenefitDays: 0,
          indemnitySurplusAccrued: 0,
          indemnitySurplusApplied: 0,
          epLumpSumPaid: 0,
          epLumpSumBasePaid: 0,
          epLumpSumDays: 0,
          epLumpSumDailyRate: 0,
          epLumpSumAppliedToCost: 0,
        },
      });
    }

    // 1. Day classification:
    //    a. 2-ADL bucket (first daysInPhase * 0.5 days, when applicable)
    //    b. Waiting period (consume the active claimant's EP balance)
    //    c. Remaining days are eligible
    const non2AdlDaysThisRow = computeNon2AdlDaysThisRow(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDaysThisRow;

    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      waitingPeriodBefore,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;

    const eligibleDaysThisRow = daysAfter2Adl - wpDaysThisRow;

    // 2. Per-row dollar math.
    const baseMonthly = getMonthlyBenefitAmountForEnvironment(
      ltcPolicy,
      phaseCarePlan.appliedCareEnvironment,
    );
    const riderForm = getRiderForm(ltcPolicy);

    // Partial-month payouts are pro-rated by eligible days, matching the
    // Asset Care / traditional-LTC strategies. A row where the waiting
    // period or the 2-ADL gate is satisfied mid-month pays for the eligible
    // slice (eligibleDays / 30) rather than stranding those days — the old
    // `=== DAYS_IN_MONTH` gate dropped them, double-truncating against the
    // phase-end tail and under-paying by up to a month per phase.
    //
    // The pool-exhaustion rule: a row pays the regular monthly benefit only
    // while the base pool can still cover a full month's benefit (carriers
    // don't cut a partial indemnity check on the final remainder). The
    // sub-monthly remainder is no longer stranded, though — on the first
    // eligible row where the pool can't fund a full month, the remainder is
    // paid out as indemnity cash and banked into the surplus reserve (P2-4a;
    // the legacy `floor(pool/baseMonthly)` stranding is consciously dropped).
    const eligibleFraction = eligibleDaysThisRow / DAYS_IN_MONTH;
    const canAffordFullMonth = openState.pools.baseBenefitPool + 1e-6 >= baseMonthly;
    const isPayingRow = eligibleDaysThisRow > 0 && canAffordFullMonth;

    // Rider escalation runs on the same anniversary clock as the row
    // (`displayPolicyYear`) — the carrier steps the rider on policy
    // anniversaries regardless of claim status (P2-1; see the header
    // comment for the accepted divergence from legacy's paid-month clock).
    let basePay = 0;
    let riderPay = 0;
    if (isPayingRow) {
      basePay = baseMonthly * eligibleFraction;
      riderPay = calcRiderMonthly(baseMonthly, displayPolicyYear, riderForm) * eligibleFraction;
    }

    // 3. Pool depletion from the regular monthly benefit.
    const baseBenefitPoolAfterBase = Math.max(openState.pools.baseBenefitPool - basePay, 0);

    // 4. EP retroactive lump. The reimbursement applies the rider step at
    //    the policy year of WP satisfaction — total paid = days × (base +
    //    rider) daily, but only the base portion depletes the base pool.
    const lumpRiderMonthly = calcRiderMonthly(baseMonthly, displayPolicyYear, riderForm);
    const epResult = computeEpRetroactiveLump({
      ltcPolicy,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      waitingPeriodBefore,
      closeHomeWp,
      closeFacilityWp,
      baseBenefitPoolAfterBase,
      baseMonthly,
      riderMonthly: lumpRiderMonthly,
      alreadyReimbursed: claimantBefore.epReimbursed,
    });
    const poolAfterLump = Math.max(baseBenefitPoolAfterBase - epResult.epLumpSumBasePaid, 0);

    // 5. Residual-pool cash-out (P2-4a): on the first eligible row where the
    //    pool can no longer fund a full month IN ANY covered environment, the
    //    sub-monthly remainder is paid out as indemnity cash (it flows into
    //    the surplus reserve below when it exceeds the row's eligible cost).
    //    Self-limiting — the pool closes at 0, so no later row can fire it
    //    again. The any-environment guard matters when per-environment
    //    benefits differ: a pool below the current (expensive) environment's
    //    monthly may still fund full months — with their rider on top — in a
    //    cheaper later environment, so cashing it out here would forfeit
    //    those rider dollars.
    const minCoveredEnvMonthly = Math.min(
      ...[
        CareEnvironment.home,
        CareEnvironment.assistedLiving,
        CareEnvironment.fullCareFacility,
        CareEnvironment.independentLiving,
      ]
        .map(env => getMonthlyBenefitAmountForEnvironment(ltcPolicy, env))
        .filter(monthly => monthly > 0),
    );
    const canAffordAnyEnvironmentMonth =
      Number.isFinite(minCoveredEnvMonthly) &&
      openState.pools.baseBenefitPool + 1e-6 >= minCoveredEnvMonthly;
    const residualPoolPayout =
      eligibleDaysThisRow > 0 && !canAffordFullMonth && !canAffordAnyEnvironmentMonth
        ? poolAfterLump
        : 0;

    const indemnityPayout = basePay + riderPay + residualPoolPayout;

    // 6. Per-month effective care cost was resolved up front (`costMetrics`)
    //    for the pre-policy gate; here we only need the eligible-day slice.
    const monthlyBenefitEligibleCost = costMetrics.dailyCareCost * eligibleDaysThisRow;

    // 7. Indemnity-vs-cost surplus accounting (per-row), then the EP lump's
    //    cost application (P2-4b): the lump covers whatever of this row's
    //    cost the regular coverage left open; any excess is BANKED into the
    //    indemnity surplus reserve instead of riding as negative ineligible
    //    cost. Totals count the lump's LTC contribution as applied-to-cost +
    //    later surplus drains — never both.
    const surplusResult = applyIndemnitySurplus({
      openSurplus: openState.strategyState.indemnitySurplus,
      indemnityPayout,
      eligibleCost: monthlyBenefitEligibleCost,
      fullCost: costMetrics.effectiveCareCost,
      // Exhaustion is the EMPTY POOL, not a $0 payout: CM II can pay $0 on a row
      // it declines to fund (it holds the pool back to cover a cheaper care
      // environment later — `residualPoolPayout` stays 0 while
      // `canAffordAnyEnvironmentMonth`). On such a row the reserve must still
      // drain. The pool only depletes (no inflation accrual), so the open balance
      // is the drawable capacity entering the row; the boundary row that empties
      // it still opens > 0 and so still drains.
      policyExhausted: openState.pools.baseBenefitPool <= 1e-6,
    });
    const costAfterCoverage = Math.max(
      costMetrics.effectiveCareCost - surplusResult.ltcPayoutToCost,
      0,
    );
    const epLumpSumAppliedToCost = Math.min(epResult.epLumpSumPaid, costAfterCoverage);
    const epLumpExcess = epResult.epLumpSumPaid - epLumpSumAppliedToCost;
    const closeSurplus = surplusResult.closeSurplus + epLumpExcess;
    const surplusAccrued = surplusResult.surplusAccrued + epLumpExcess;

    // 8. Closing balances. Only the base portion of the lump depletes the
    //    pool / death benefit; the rider portion is paid on top (matches CM
    //    II's regular monthly base+rider split where rider does not draw
    //    from the base pool). The residual cash-out is base dollars, so it
    //    depletes both.
    const closeBaseBenefitPool = Math.max(poolAfterLump - residualPoolPayout, 0);
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const dbRawDrawdown = basePay + epResult.epLumpSumBasePaid + residualPoolPayout;
    const closeDeathBenefit = Math.max(
      openState.pools.deathBenefit - dbRawDrawdown,
      minGuaranteed,
    );
    const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
    const deathBenefitFloored =
      closeDeathBenefit === minGuaranteed && dbRawDrawdown > deathBenefitReduction;

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays: non2AdlDaysThisRow,
      coverageEligibleDays: eligibleDaysThisRow,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      // CM II's per-row benefit ceiling is base + rider at the row's
      // anniversary clock (P2-1 — one clock for every row, paying or not).
      // `lumpRiderMonthly` is calcRiderMonthly at exactly that clock.
      appliedMonthlyBenefitCap: baseMonthly + lumpRiderMonthly,
      appliedCapPoolSource: 'base',
    };

    // Net out-of-pocket: cost − regular coverage − the lump portion that
    // covered THIS row's cost. Never negative — the lump's excess is banked
    // as surplus above instead of riding as a negative OOP credit (P2-4b).
    const ineligibleCost =
      costMetrics.effectiveCareCost - surplusResult.ltcPayoutToCost - epLumpSumAppliedToCost;

    const payout: PayoutSummary = {
      ltcCoveragePaid: surplusResult.ltcCoveragePaid,
      ltcPayoutToCost: surplusResult.ltcPayoutToCost,
      ineligibleCost,
      indemnityPayout,
    };

    const deltas: MonthlyDeltas<CmTwoDeltas> = {
      payout,
      // CM II's base pool drains by the regular monthly base benefit plus
      // the base portion of any EP retroactive lump on the same row, plus
      // any residual-pool cash-out. The rider portion is paid on top — it
      // does not deplete the pool.
      base: {
        depletion: basePay + epResult.epLumpSumBasePaid + residualPoolPayout,
        inflationAccrual: 0,
      },
      eob: { depletion: 0, inflationAccrual: 0 },
      deathBenefit: {
        reduction: deathBenefitReduction,
        flooredAtMinimum: deathBenefitFloored,
      },
      strategyDeltas: {
        baseBenefitPaid: basePay + residualPoolPayout,
        riderBenefitPaid: riderPay,
        baseBenefitDays: isPayingRow ? eligibleDaysThisRow : 0,
        riderBenefitDays: isPayingRow ? eligibleDaysThisRow : 0,
        indemnitySurplusAccrued: surplusAccrued,
        indemnitySurplusApplied: surplusResult.surplusApplied,
        epLumpSumPaid: epResult.epLumpSumPaid,
        epLumpSumBasePaid: epResult.epLumpSumBasePaid,
        epLumpSumDays: epResult.epLumpSumDays,
        epLumpSumDailyRate: epResult.epLumpSumDailyRate,
        epLumpSumAppliedToCost,
      },
    };

    const claimantAfter: CmTwoClaimantState = {
      epReimbursed: claimantBefore.epReimbursed || epResult.fired,
    };
    const activeWaitingPeriodAfter: WaitingPeriodState = {
      homeDays: closeHomeWp,
      facilityDays: closeFacilityWp,
    };

    // indemnitySurplus is shared; the flat EP latch mirrors the active
    // claimant (the `joint` sub-object below is the source of truth — A9).
    const closeStrategyStateBase = {
      indemnitySurplus: closeSurplus,
      epReimbursed: claimantAfter.epReimbursed,
    };
    // `joint` and `jointWaitingPeriod` are an invariant pair (seeded together in
    // initClaimState). On a joint row the active claimant is updated and the
    // other spouse carries forward unchanged.
    const closeStrategyState: CmTwoState =
      jointBefore && jointWaitingPeriodBefore
        ? {
            ...closeStrategyStateBase,
            joint: { ...jointBefore, [activeClaimant]: claimantAfter },
            jointWaitingPeriod: {
              ...jointWaitingPeriodBefore,
              [activeClaimant]: activeWaitingPeriodAfter,
            },
          }
        : closeStrategyStateBase;

    const closeState: MonthlyState<CmTwoState> = {
      pools: {
        baseBenefitPool: closeBaseBenefitPool,
        extensionOfBenefitsPool: 0,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: activeWaitingPeriodAfter,
      strategyState: closeStrategyState,
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closeBaseBenefitPool <= 0,
      clientPoolExhausted: closeBaseBenefitPool <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    // Transition milestones (phase/env change, anniversary, claim-start) compare
    // against `priorClaimantMonth` — the prior row of THIS spouse — not the
    // global previous row, which on a joint policy is often the other spouse
    // (the merged stream interleaves them when their care overlaps) and would
    // fire a spurious transition on every interleaved row.
    const milestones = deriveStandardMilestones<CmTwoState, CmTwoDeltas>({
      context,
      facts,
      openState: rowOpenState,
      closeState,
      deltas,
      priorMonth: priorClaimantMonth,
      retroactiveLumpPaid: epResult.epLumpSumPaid,
      minimumGuaranteedDeathBenefit: minGuaranteed,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: riderForm.rate > 0,
    });

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState: rowOpenState,
      closeState,
      payout,
    };
    const ineligibilityReason = deriveIneligibilityReason<CmTwoState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<CmTwoState>(ineligibilityInput);

    return {
      context,
      facts,
      openState: rowOpenState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  finalizeDeathBenefit({
    ltcPolicy,
    closingState,
  }: FinalizeDeathBenefitInput<CmTwoState>): number {
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;
    const baseDollarsPaid = Math.max(faceAmount - closingState.pools.deathBenefit, 0);
    return calcCareMattersDeathBenefit({
      specifiedAmount: faceAmount,
      baseDollarsPaid,
      storedFloorAmount: ltcPolicy.policyMinimumGuaranteedBenefitAmount,
    });
  },
};
